import { baseUrl } from '../http';
import axios from 'axios';
import { serverDateLocale } from '../../utils';
import moment from 'moment';

let tripUrl = `${baseUrl}/trip`;

export const tripApi = {
  search(searchText, startDate, endDate, status, pagination) {
    return axios.post(`${tripUrl}/search${parsePagination(pagination)}`,
      {
        search: searchText,
        searchStartDate: startDate,
        searchEndDate: endDate,
        status: status
      })
      .then(res => {
        if (res.data.success) { return res.data; }
        return Promise.reject(new Error('Cannot load trips'));
      });
  },

  getTrip(tripId) {
    return axios.get(`${tripUrl}/${tripId}`)
      .then(({ data: { success, trip } }) => {
        if (success) {
          const startDate = moment(trip.startDate, serverDateLocale);
          const endDate = moment(trip['endDate'], serverDateLocale);
          return {
            ...trip,
            startDate,
            endDate
          };
        }
      });
  },

  getAllParticipants(tripId) {
    let pagination = { perPage: 150, currentPage: 1 };
    return axios.get(`${tripUrl}/${tripId}/participant/all${parsePagination(pagination)}`)
      .then(({ data: { success, trip, participants } }) => {
        if (!success) { return Promise.reject(new Error('Server error.')); }
        return participants.map(p => {
          let identityCardCreationDate = moment(p.person.identityCardCreationDate, serverDateLocale);
          let identityCardExpirationDate = moment(p.person.identityCardExpirationDate, serverDateLocale);
          let passportCreationDate = moment(p.person.passportCreationDate, serverDateLocale);
          let passportExpirationDate = moment(p.person.passportExpirationDate, serverDateLocale);
          return {
            ...p,
            person: {
              ...p.person,
              identityCardCreationDate,
              identityCardExpirationDate,
              passportCreationDate,
              passportExpirationDate
            }
          };
        });
      });
  },

  async getAllExpenses(tripId) {
    let pagination = { perPage: 150, currentPage: 1 };
    const { data: { success, expenses } } = await axios.get(`${tripUrl}/${tripId}/expense/all${parsePagination(pagination)}`);
    if (!success) { return Promise.reject(new Error('Server error.')); }
    return expenses.map(p => {
      let issueDate = moment(p.issueDate, serverDateLocale);
      return {
        ...p,
        issueDate
      };
    });
  },

  createExpense(tripId, expenseData) {
    return axios.put(`${tripUrl}/${tripId}/expense/create`, expenseData)
      .then(res => {
        if (res.data.success) {
          let expense = res.data.expense;
          let issueDate = moment(expense.issueDate, serverDateLocale);
          return { ...expense, issueDate };
        }
        return Promise.reject(new Error('Cannot create expense.'));
      });
  },

  create(tripData) {
    return axios.put(`${tripUrl}/create`, tripData)
      .then(res => {
        if (res.data.success) { return res.data.trip; }
        return Promise.reject(new Error('Cannot create trip.'));
      });
  },

  convertToEur(tripId) {
    return axios.post(`${tripUrl}/${tripId}/convert-to-eur`)
      .then(({ data }) => {
        if (data.success) {
          return data;
        }
        return Promise.reject(new Error(data.message || 'Cannot convert trip.'));
      })
      .catch(error => {
        const message = error.response?.data?.badRequest || error.response?.data?.message || error.message || 'Cannot convert trip.';
        return Promise.reject(new Error(message));
      });
  },

  uploadDocument: (documentInfo) => {
    let formData = new FormData();
    formData.append('documentType', documentInfo.documentType);
    formData.append('document', documentInfo.document);
    return axios.post(`${tripUrl}/document/`, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
      .then(res => {
        if (res.data.success) return res.data.document;
        return Promise.reject(new Error('Cannot upload document.'));
      });
  },

  updateField: (tripId, attribute, value) => {
    if (attribute === 'startDate' || attribute === 'endDate') {
      value = value.format(serverDateLocale);
    }
    return axios.post(`${tripUrl}/${tripId}`, {
      attribute: attribute,
      value: value
    })
      .then(res => {
        if (res.data.success) { return res.data.trip; }
        return Promise.reject(new Error('Cannot update data.'));
      });
  },

  updateExpenseField: (tripId, expenseId, attribute, value) => {
    if (attribute === 'startDate' || attribute === 'endDate') {
      value = value.format(serverDateLocale);
    }
    return axios.post(`${tripUrl}/${tripId}/expense/${expenseId}`, {
      attribute: attribute,
      value: value
    })
      .then(res => {
        if (res.data.success) { return res.data.expense; }
        return Promise.reject(new Error('Cannot update expense.'));
      });
  },

  linkContract: (tripId, docId) => {
    return tripApi.updateField(tripId, 'tripContractTemplate', docId);
  },

  linkTripDetails: (tripId, docId) => {
    return tripApi.updateField(tripId, 'tripDetails', docId);
  },

  getDocument: (tripId, docId) => {
    return axios.get(`${tripUrl}/${tripId}/document/${docId}`)
      .then(res => {
        if (res.data.success) {
          return res.data.file;
        }
        return Promise.reject(new Error('Cant get file'));
      });
  },

  removeExpense: (tripId, expenseId) => {
    return axios.delete(`${tripUrl}/${tripId}/expense/${expenseId}`)
      .then(res => {
        if (res.data.success)  {
          return res.data.expense;
        }
        return Promise.reject(new Error('Cant remove the expense record.'));
      });
  },

  async updateTravelDetails(tripId, participantId, attributeName, attributeValue) {
    return axios
      .post(
        `${baseUrl}/trip/${tripId}/participant/${participantId}`,
        {
          attribute: attributeName,
          value: attributeValue
        })
      .then(res => {
        if (res.data.success === false) { return Promise.reject(new Error('cannot make changes')); }
        return res;
      });
  }
};

function parsePagination(pagination) {
  if (pagination === undefined || pagination == null) {
    return '';
  }
  return `?items=${pagination.perPage}&page=${pagination.currentPage - 1}`;
}
